import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLang } from '@/i18n/LanguageContext';

const N8N_WEBHOOK_URL = "https://n8n.ias-n8n.cloud/webhook/rdaa-contacto";

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: ''
};

const WaitlistModal = ({ open, onClose, cohort }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');

  useEffect(() => {
    if (!open) {
      const t1 = setTimeout(() => {
        setForm(initialForm);
        setErrors({});
        setState('idle');
      }, 250);
      return () => clearTimeout(t1);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = t.lead.required;
    if (!form.lastName.trim()) e.lastName = t.lead.required;
    if (!form.email.trim()) e.email = t.lead.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.lead.invalidEmail;
    if (!form.phone.trim()) e.phone = t.lead.required;
    else if (form.phone.replace(/\D/g, '').length < 7) e.phone = t.lead.invalidPhone;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setState('loading');
    try {
      const res = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'waitlist',
          ...form,
          cohortId: cohort?.id,
          campus: cohort?.campusLabel,
          classType: cohort?.typeLabel,
          startDate: cohort?.startDate,
          endDate: cohort?.endDate,
          language: lang
        })
      });
      if (!res.ok) throw new Error('Webhook error');
      setState('success');
    } catch {
      setState('error');
    }
  };

  const onChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
          <motion.div
            initial={{ y: 30, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-gradient-to-br from-slate-900 to-blue-950 text-white px-6 pt-6 pb-5">
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em] mb-3">
                <Clock className="w-3 h-3" /> Waitlist
              </div>
              <h3 className="text-2xl font-bold leading-tight">{t.waitlist.title}</h3>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">{t.waitlist.subtitle}</p>
            </div>

            <div className="p-6">
              {state === 'success' ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900">{t.waitlist.success}</h4>
                  <p className="text-slate-600 mt-2">{t.waitlist.successDesc}</p>
                  <Button
                    onClick={onClose}
                    className="mt-6 w-full bg-brand-primary hover:opacity-80 text-white rounded-xl h-11"
                  >
                    {t.waitlist.close}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  {cohort && (
                    <div className="mb-1 rounded-xl bg-blue-50 border border-blue-100 px-3 py-2.5 text-[12.5px]">
                      <div className="font-bold text-blue-900">{t.waitlist.classLabel}</div>
                      <div className="text-blue-800">
                        {cohort.typeLabel} — {cohort.campusLabel} — {cohort.startDate}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t.waitlist.firstName} error={errors.firstName}>
                      <Input
                        value={form.firstName}
                        onChange={onChange('firstName')}
                        className="h-11 rounded-xl border-slate-200"
                      />
                    </Field>
                    <Field label={t.waitlist.lastName} error={errors.lastName}>
                      <Input
                        value={form.lastName}
                        onChange={onChange('lastName')}
                        className="h-11 rounded-xl border-slate-200"
                      />
                    </Field>
                  </div>
                  <Field label={t.waitlist.email} error={errors.email}>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                      className="h-11 rounded-xl border-slate-200"
                    />
                  </Field>
                  <Field label={t.waitlist.phone} error={errors.phone}>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={onChange('phone')}
                      className="h-11 rounded-xl border-slate-200"
                    />
                  </Field>

                  {state === 'error' && (
                    <div className="flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm px-3 py-2.5">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{t.waitlist.error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={state === 'loading'}
                    className="w-full mt-2 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 text-white rounded-xl h-11 font-semibold"
                  >
                    {state === 'loading' ? t.waitlist.submitting : t.waitlist.submit}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <Label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">{label}</Label>
    {children}
    {error && <p className="text-[12px] text-rose-600 mt-1">{error}</p>}
  </div>
);

export default WaitlistModal;
